<template>
  <CCard>
    <div
      v-if="isLoading"
      :style="{ height: '20vh' }"
      class="d-flex flex-column justify-content-center align-items-center"
    >
      <CSpinner
        class="custom-spinner"
        color="info"
        grow
      />
    </div>

    <div v-else>
      <div class="d-flex align-items-center justify-content-between">
        <CCallout color="info">
          <small class="text-muted">Total Accounts:</small><br>
          <strong class="h4">
            {{ requestsFormatted.length }}
          </strong>
        </CCallout>

        <div
          v-if="hasRestrictedCountries"
          v-c-tooltip="{
            content: `Hides applications from: ${restrictedCountriesLabel}. Checks IP Country first; if empty, checks Country.`,
            placement: 'top'
          }"
          class="mx-3 d-flex align-items-center"
        >
          <CInputCheckbox
            label="Hide restricted countries"
            custom
            :checked.sync="hideRestrictedCountries"
            class="restricted-countries-checkbox"
          />
        </div>
      </div>
      <CDataTable
        data-cy="table-registration-requests"
        small
        border
        sorter
        column-filter
        :items="requestsFormatted"
        :fields="fields"
        :items-per-page="10"
        :pagination="{align: 'center'}"
        :sorter-value="{ column: 'requestedOn', asc: false }"
      >
        <template #email-filter>
          <input
            v-model="searchEmail"
            class="form-control form-control-sm"
          >
        </template>
    
        <template #status-filter>
          <select
            v-model="status"
            class="form-control form-control-sm"
            @change="onStatusChange"
          >
            <option
              value="All"
              selected="selected"
            />
            <option value="Pending">
              Pending
            </option>
            <option value="Waitlisted">
              Waitlisted
            </option>
          </select>
        </template>

        <template #email="{item}">
          <td>{{ item.representative.email }}</td>
        </template>
        
        <template #companyName="{item}">
          <td>
            {{ item.companyName }}

            <CBadge
              v-if="item.testOnly"
              color="danger"
              class="ml-1"
            >
              Test
            </CBadge>
          </td>
        </template>

        <template #requestedOn="{item}">
          <td class="text-nowrap">
            {{ item.formattedRequestedOn }}
          </td>
        </template>

        <template #lastUpdatedAt="{item}">
          <td>
            {{ item.formattedLastUpdatedAt }}
          </td>
        </template>

        <template #technology="{item}">
          <td class="text-nowrap">
            {{ getSafe(() => item.callTechnology) }}
          </td>
        </template>

        <template #country="{item}">
          <td class="text-nowrap">
            <span
              v-if="isRestrictedCountry(getSafe(() => item.companyAddress.countryCode))"
              :title="`Restricted country: ${getSafe(() => item.companyAddress.countryCode)}`"
            >
              <CIcon
                name="cil-warning"
                class="text-danger mr-1"
              />
            </span>
            {{ getSafe(() => item.companyAddress.countryCode) }}
          </td>
        </template>

        <template #ipCountry="{item}">
          <td class="text-nowrap">
            <span
              v-if="isRestrictedCountry(getSafe(() => item.geoIpInfo.ipCountry))"
              :title="`Restricted country: ${getSafe(() => item.geoIpInfo.ipCountry)}`"
            >
              <CIcon
                name="cil-warning"
                class="text-danger mr-1"
              />
            </span>
            {{ getSafe(() => item.geoIpInfo.ipCountry) }}
          </td>
        </template>

        <template #representative="{item}">
          <td class="text-nowrap">
            {{ getSafe(() => `${item.representative.firstName} ${item.representative.lastName}`) }}
          </td>
        </template>

        <template #status="{item}">
          <td>
            <CBadge :color="getBadge(item.status)">
              {{ item.status }}
            </CBadge>
          </td>
        </template>

        <template #Actions="{item}">
          <td>
            <CButton
              color="info"
              size="sm"
              :to="`/dashboard/registration-requests/${type}/${item.id}`"
              class="mr-2 mt-2"
            >
              View Application
            </CButton>

            <CDropdown
              v-if="type.toLowerCase() === 'buyer'"
              color="primary"
              size="sm"
              class="mt-2"
              :add-toggler-classes="isSetPendingItemLoading[item.id] ? 'c-loading-button c-loading-button-loading' : ''"
              :disabled="isSetPendingItemLoading[item.id]"
            >
              <template #toggler-content>
                <CSpinner
                  size="sm"
                  class="c-loading-button-spinner"
                />
                Set Pending Check
              </template>
              <CDropdownItem
                v-for="itemConfig in buyerRegistrationChecklistItemsConfig"
                :key="itemConfig.id"
                @click="setPendingItem(itemConfig, '', item.id)"
              >
                {{ itemConfig.name }}
              </CDropdownItem>

              <CDropdownItem
                @click="setPendingItem({}, 'Stripe Verification', item.id)"
              >
                Stripe Verification
              </CDropdownItem>
            </CDropdown>

            <CDropdown
              v-if="type.toLowerCase() === 'seller'"
              color="primary"
              size="sm"
              class="mt-2"
              :add-toggler-classes="isSetPendingItemLoading[item.id] ? 'c-loading-button c-loading-button-loading' : ''"
              :disabled="isSetPendingItemLoading[item.id]"
            >
              <template #toggler-content>
                <CSpinner
                  size="sm"
                  class="c-loading-button-spinner"
                />
                Set Pending Check
              </template>
              <CDropdownItem
                v-for="itemConfig in sellerRegistrationChecklistItemsConfig"
                :key="itemConfig.id"
                @click="setPendingItem(itemConfig, '', item.id)"
              >
                {{ itemConfig.name }}
              </CDropdownItem>

              <CDropdownItem
                @click="setPendingItem({}, 'Stripe Verification', item.id)"
              >
                Stripe Verification
              </CDropdownItem>
            </CDropdown>
          </td>
        </template>
      </CDataTable>
    </div>
  </CCard>
</template>

<script>
import { dateTimeFormat } from '@/utils'
import statusMixin from '@/mixins/status-mixin'
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: 'RegistrationRequestsList',

  mixins: [statusMixin],

  props: {
    registrationRequests: {
      type: Array,
      default() {
        return []
      }
    },

    isLoading: {
      type: Boolean,
      default: false
    },

    type: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      searchEmail: '',
      status:'',
      hideRestrictedCountries: true,
      isSetPendingItemLoading: {},
      fields: [
        { key: 'status', label: 'Status' },
        { key: 'requestedOn', label: 'Applied', sorter: true },
        { key: 'lastUpdatedAt', label: 'Last Edited At' },
        { key: 'companyName', label: 'Company' },
        { key: 'technology', label: 'Technology', sortable: true },
        { key: 'country', label: 'Country', sortable: true },
        { key: 'ipCountry', label: 'IP Country', sortable: true },
        { key: 'representative', label: 'Representative', sortable: true },
        { key: 'nextPendingItemForFinalApproval', label: 'Next Pending Item', sorter: true },
        { key: 'Actions', filter: false }
      ]
    }
  },
 
  computed: {
    ...mapState('registrationChecklistItem', [
      'sellerRegistrationChecklistItemsConfig',
      'buyerRegistrationChecklistItemsConfig'
    ]),

    ...mapGetters('country', [
      'hasRestrictedCountries',
      'restrictedCountriesLabel',
      'isRestrictedCountry'
    ]),

    requestsFormatted() {
      return this.registrationRequests.filter(request => {
        if (this.hideRestrictedCountries && this.isRestrictedRequest(request)) {
          return false
        }

        if (!this.searchEmail) {
          return true
        }

        return request.representative.email.toLowerCase().includes(this.searchEmail.toLowerCase().trim())
      }).map(request => {
        return {
          ...request,
          formattedRequestedOn: dateTimeFormat.format(new Date(request.requestedOn)),
          formattedLastUpdatedAt: request.lastUpdatedAt ? dateTimeFormat.format(new Date(request.lastUpdatedAt)) : '',
          nextPendingItemForFinalApproval: request.nextPendingItemForFinalApproval != null ? request.nextPendingItemForFinalApproval : '',
        }
      })
    }
  },

  async created() {
    this.getRestrictedCountries()
    await this.getRegistrationChecklistItemsConfig(this.type)
  },

  mounted() {
    // update data when mounting the component
    if ("status" in this.$route.query) {
      this.status = this.$route.query.status
    }
  },

  methods: {
    ...mapActions('registrationChecklistItem', [
      'getRegistrationChecklistItemsConfig',
    ]),

    ...mapActions('registrationRequests', [
      'setPendingStatusForRegistrationOrAccount',
    ]),

    ...mapActions('country', [
      'getRestrictedCountries',
    ]),

    isRestrictedRequest(request) {
      const ipCountry = request?.geoIpInfo?.ipCountry
      const country = request?.companyAddress?.countryCode

      // Check IP Country first; fall back to Country when IP Country is empty
      return this.isRestrictedCountry(ipCountry || country)
    },

    onStatusChange() {
     
      const query = {}
      query.status = this.status
      
      this.$router.push({ query: query })

      this.$emit("status-change", this.status)
      
    },

    async setPendingItem(itemConfig, miscellaneousItem, registrationId) {
      try {
        this.$set(this.isSetPendingItemLoading, registrationId, true)

        await this.setPendingStatusForRegistrationOrAccount({
          registrationId, 
          itemConfigId: itemConfig.id,
          miscellaneousPendingItem: miscellaneousItem,
        })

        this.$emit('refresh-requests')
      } catch {
        //
      } finally {
        this.$set(this.isSetPendingItemLoading, registrationId, false)
      }
    },
  }

}
</script>

